import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import img from "./all_bg.jpg";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const BACKEND_URL = "https://api.bbsfacultyprofile.com";
const TABLE_NAME = "research_supervision";

const ResearchSupervision = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [recordId, setRecordId] = useState(null);

  const [formData, setFormData] = useState({
    start_year: null,
    end_year: null,
    thesis_title: "",
    research_role: "",
    degree: "",
    institution: "",
    country: "",
  });

  // ================= Fetch =================
const fetchRecords = async () => {
  try {
    const res = await fetch(`${BACKEND_URL}/api/${TABLE_NAME}`, {
      method: "GET",
      credentials: "include",
    });

    if (!res.ok) throw new Error("Failed to fetch records");

    const data = await res.json();

    const processed = (Array.isArray(data) ? data : [])
      .map((rec) => ({
        ...rec,
        start_year: rec.start_year ? new Date(rec.start_year) : null,
        end_year: rec.end_year ? new Date(rec.end_year) : null,
      }))
      .sort((a, b) => {
        // Sort using start_year (latest first)
        const dateA = a.start_year ? a.start_year.getTime() : 0;
        const dateB = b.start_year ? b.start_year.getTime() : 0;

        return dateB - dateA;
      });

    setRecords(processed);

  } catch (err) {
    toast.error(err.message);
  }
};

  useEffect(() => {
    fetchRecords();
  }, []);

  // ================= Helpers =================
  const formatMonthYear = (dateStr) => {
    if (!dateStr) return "";
    return format(new Date(dateStr), "MM/yyyy");
  };

  // ================= Handlers =================
  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        ...formData,
        start_year: formData.start_year
          ? format(formData.start_year, "yyyy-MM") + "-01"
          : null,
        end_year: formData.end_year
          ? format(formData.end_year, "yyyy-MM") + "-01"
          : null,
      };

      const url = recordId
        ? `${BACKEND_URL}/api/${TABLE_NAME}/${recordId}`
        : `${BACKEND_URL}/api/${TABLE_NAME}`;

      const res = await fetch(url, {
        method: recordId ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Failed to save record");

      toast.success("Record saved successfully!");

      setFormData({
        start_year: null,
        end_year: null,
        thesis_title: "",
        research_role: "",
        degree: "",
        institution: "",
        country: "",
      });

      setRecordId(null);
      fetchRecords();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleEdit = (rec) => {
    setFormData({
      start_year: rec.start_year ? new Date(rec.start_year) : null,
      end_year: rec.end_year ? new Date(rec.end_year) : null,
      thesis_title: rec.thesis_title || "",
      research_role: rec.research_role || "",
      degree: rec.degree || "",
      institution: rec.institution || "",
      country: rec.country || "",
    });
    setRecordId(rec.id);
    toast.info("Editing record...");
  };

  const handleDelete = (rec) => {
    const toastId = toast.info(
      <div>
        Delete this record?
        <button
          onClick={async () => {
            try {
              const res = await fetch(
                `${BACKEND_URL}/api/${TABLE_NAME}/${rec.id}`,
                { method: "DELETE", credentials: "include" }
              );
              if (!res.ok) throw new Error("Failed to delete record");
              fetchRecords();
              toast.update(toastId, {
                render: "Record deleted successfully!",
                type: "success",
                autoClose: 3000,
              });
            } catch (err) {
              toast.update(toastId, {
                render: err.message,
                type: "error",
                autoClose: 3000,
              });
            }
          }}
          style={{
            marginLeft: "10px",
            padding: "3px 8px",
            background: "#ef4444",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Confirm
        </button>
      </div>,
      { autoClose: false, closeOnClick: false }
    );
  };

  // ================= UI =================
  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "40px 20px",
        fontFamily: "Arial, sans-serif",
        backgroundImage: `url(${img})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      <ToastContainer position="top-center" autoClose={3000} />

      <div
        style={{
          maxWidth: "800px",
          margin: "0 auto",
          background: "#fff",
          padding: "30px 40px",
          borderRadius: "10px",
          boxShadow: "0px 0px 12px rgba(0,0,0,0.1)",
        }}
      >
        <h2
          style={{
            textAlign: "center",
            color: "#1e3a8a",
            marginBottom: "25px",
          }}
        >
          Graduate Research Supervision
        </h2>

        <form
          onSubmit={handleSubmit}
          style={{ display: "flex", flexDirection: "column" }}
        >
          {/* Dates */}
          <div style={{ display: "flex", gap: "10px" }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontWeight: "bold" }}>From (mm/yyyy)</label>
              <DatePicker
                selected={formData.start_year}
                onChange={(date) =>
                  setFormData({ ...formData, start_year: date })
                }
                dateFormat="MM/yyyy"
                showMonthYearPicker
                placeholderText="Select month/year"
              />
            </div>

            <div style={{ flex: 1 }}>
              <label style={{ fontWeight: "bold" }}>To (mm/yyyy)</label>
              <DatePicker
                selected={formData.end_year}
                onChange={(date) =>
                  setFormData({ ...formData, end_year: date })
                }
                dateFormat="MM/yyyy"
                showMonthYearPicker
                placeholderText="Select month/year"
              />
            </div>
          </div>

          {/* Inputs */}
          {[
            { label: "Thesis Title", name: "thesis_title" },
            { label: "Institution", name: "institution" },
            { label: "Country", name: "country" },
          ].map(({ label, name }) => (
            <div key={name}>
              <label style={{ fontWeight: "bold", marginTop: "10px" }}>
                {label}
              </label>
              <input
                type="text"
                name={name}
                value={formData[name]}
                onChange={handleChange}
                style={{
                  padding: "10px",
                  marginTop: "5px",
                  border: "1px solid #ccc",
                  borderRadius: "6px",
                  width: "100%",
                }}
              />
            </div>
          ))}

          {/* Selects */}
          {[
            {
              label: "Role",
              name: "research_role",
              options: [
                "Supervisor-Main",
                "Co-Supervisor",
                "Examiner-Chair",
                "Examiner-Member",
              ],
            },
            {
              label: "Degree",
              name: "degree",
              options: ["Masters", "MPhil", "PhD"],
            },
          ].map(({ label, name, options }) => (
            <div key={name}>
              <label style={{ fontWeight: "bold", marginTop: "10px" }}>
                {label}
              </label>
              <select
                name={name}
                value={formData[name]}
                onChange={handleChange}
                style={{
                  padding: "10px",
                  marginTop: "5px",
                  border: "1px solid #ccc",
                  borderRadius: "6px",
                  width: "100%",
                }}
              >
                <option value="">Select {label}</option>
                {options.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </div>
          ))}

          <button
            type="submit"
            style={{
              background: "#3b82f6",
              color: "white",
              padding: "12px",
              border: "none",
              borderRadius: "6px",
              marginTop: "20px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            {recordId ? "Update Record" : "Add Record"}
          </button>
        </form>

        {/* Records Table */}
        {records.length > 0 && (
          <div style={{ marginTop: "30px", overflowX: "auto" }}>
            <h3 style={{ color: "#1e3a8a" }}>Your Records</h3>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                marginTop: "10px",
              }}
            >
              <thead>
                <tr>
                  {[
                    "From",
                    "To",
                    "Thesis Title",
                    "Role",
                    "Degree",
                    "Institution",
                    "Country",
                    "Actions",
                  ].map((h) => (
                    <th
                      key={h}
                      style={{ border: "1px solid #ccc", padding: "8px" }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {records.map((rec) => (
                  <tr key={rec.id}>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {formatMonthYear(rec.start_year)}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {formatMonthYear(rec.end_year)}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.thesis_title}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.research_role}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.degree}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.institution}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.country}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ccc",
                        padding: "8px",
                        textAlign: "center",
                      }}
                    >
                      <button
                        onClick={() => handleEdit(rec)}
                        style={{
                          background: "#2563eb",
                          color: "white",
                          border: "none",
                          borderRadius: "5px",
                          padding: "5px 10px",
                          marginRight: "5px",
                          cursor: "pointer",
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(rec)}
                        style={{
                          background: "#ef4444",
                          color: "white",
                          border: "none",
                          borderRadius: "5px",
                          padding: "5px 10px",
                          cursor: "pointer",
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ textAlign: "center", marginTop: "25px" }}>
          <button
            onClick={() => navigate("/dashboard")}
            style={{
              background: "#1e3a8a",
              color: "white",
              padding: "10px 20px",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResearchSupervision;
